import { isAxiosError } from "axios";

/** The server's machine code of a refusal (`detail.code`), for the 409/413 codes of §3.2. */
export function attachmentSetErrorCode(error: unknown): string | null {
    if (error instanceof WorkingDocumentUnavailable) return error.code;
    if (!isAxiosError(error)) return null;
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (detail && typeof detail === "object" && "code" in detail) {
        const code = (detail as { code: unknown }).code;
        return typeof code === "string" ? code : null;
    }
    return null;
}

/**
 * A file the plain-file command refused because the set takes it as pages (422 `pages_not_file`):
 * the caller adds it to the set instead.
 */
export const isPagesNotFile = (error: unknown): boolean =>
    attachmentSetErrorCode(error) === "pages_not_file";

/** A stale `expected_version` (HTTP 409 with `current_version`), or null. */
export function attachmentSetConflictVersion(error: unknown): number | null {
    if (!isAxiosError(error) || error.response?.status !== 409) return null;
    const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
    if (detail && typeof detail === "object" && "current_version" in detail) {
        const version = (detail as { current_version: unknown }).current_version;
        return typeof version === "number" ? version : null;
    }
    return null;
}

/** The refusals whose `detail` the server words for the person (the attachment set contract). */
const WORDED_STATUSES = new Set([403, 409, 413, 422]);

/**
 * The sentence the server wrote for a refusal ("Only the author can change this draft", "That PDF
 * is password protected and cannot be added"), or null: a string `detail`, or the `message` of a
 * `{code, message}` one, on a 403, 409, 413 or 422. It says why, so the screens show it as written
 * (`attachmentErrorMessage`, `describePageFailure`): the client keeps no words of its own for a
 * refusal the server words. A request the server could not parse (a 422 whose `detail` is a list)
 * has no sentence.
 */
export function attachmentSetRefusalSentence(error: unknown): string | null {
    // A refusal read by fetch (the working document, extracted pages, a sent PDF) carries the same.
    if (error instanceof WorkingDocumentUnavailable)
        return WORDED_STATUSES.has(error.status) && error.message.trim() ? error.message : null;
    if (!isAxiosError(error) || !WORDED_STATUSES.has(error.response?.status ?? 0)) return null;
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    const sentence =
        detail && typeof detail === "object" && "message" in detail
            ? (detail as { message: unknown }).message
            : detail;
    return typeof sentence === "string" && sentence.trim() ? sentence : null;
}

/**
 * The fields of a request the server could not take as sent (a 422 whose `detail` is a list, one
 * entry per field, its `loc` ending with the field's name: `width_in`, `count`), or none for any
 * other answer. The server words these refusals in no sentence; the screens say which bound it was.
 */
export function attachmentSetRefusedFields(error: unknown): readonly string[] {
    if (!isAxiosError(error) || error.response?.status !== 422) return [];
    const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
    if (!Array.isArray(detail)) return [];
    return detail.flatMap((entry: unknown) => {
        const loc = entry && typeof entry === "object" ? (entry as { loc?: unknown }).loc : null;
        const field = Array.isArray(loc) ? loc[loc.length - 1] : null;
        return typeof field === "string" ? [field] : [];
    });
}

/**
 * A write placed by position (a move, an insert) queued behind a move that did not go through: its
 * place was worked out against an order the server never took, so it is not sent (the set is read
 * again). Its message is said as it is.
 */
export class OrderChangedMeanwhile extends Error {
    constructor() {
        super(
            "A move just before this did not go through, so the pages were read again. Try again.",
        );
        this.name = "OrderChangedMeanwhile";
    }
}

/** The working document asked for is no longer the set's (409 `document_changed`). */
export class WorkingDocumentChanged extends Error {
    constructor(readonly contentHash: string | null) {
        super("The pages changed since this document was described.");
        this.name = "WorkingDocumentChanged";
    }
}

/** The working document's bytes could not be read, with the server's code and sentence. */
export class WorkingDocumentUnavailable extends Error {
    constructor(
        readonly status: number,
        readonly code: string | null,
        message: string,
    ) {
        super(message);
        this.name = "WorkingDocumentUnavailable";
    }
}
